import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import { type EmberChangeset } from 'ember-changeset';
import { HeadlessForm, type FormValidateCallback } from 'ember-headless-form';
import { validateChangeset } from 'ember-headless-form-changeset';
import type { HeadlessFormComponentSignature } from 'ember-headless-form/components/headless-form';
import type CheckboxFieldComponent628 from '../-private/components/form/fields/checkbox';
import CheckboxFieldComponent from '../-private/components/form/fields/checkbox.gts';
import type InputFieldComponent628 from '../-private/components/form/fields/input';
import type InputRowComponent628 from '../-private/components/form/fields/input-row';
import InputRowComponent from '../-private/components/form/fields/input-row.gts';
import InputFieldComponent from '../-private/components/form/fields/input.gts';
import type RadioGroupFieldComponent628 from '../-private/components/form/fields/radio-group';
import RadioGroupFieldComponent from '../-private/components/form/fields/radio-group.gts';
import type RadioRowFieldComponent628 from '../-private/components/form/fields/radio-row';
import RadioRowFieldComponent from '../-private/components/form/fields/radio-row.gts';
import type TextareaComponent628 from '../-private/components/form/fields/textarea';
import type TextareaRowComponent628 from '../-private/components/form/fields/textarea-row';
import TextareaRowComponent from '../-private/components/form/fields/textarea-row.gts';
import TextareaComponent from '../-private/components/form/fields/textarea.gts';
import type ToggleButtons628 from '../-private/components/form/fields/toggle-buttons';
import ToggleButtons from '../-private/components/form/fields/toggle-buttons.gts';
import type FieldsetRowComponent628 from '../-private/components/form/fieldset-row';
import FieldsetRowComponent from '../-private/components/form/fieldset-row.gts';
import RowComponent from '../-private/components/form/row.gts';
import type SubmitButtonComponent628 from '../-private/components/form/submit-button';
import SubmitButtonComponent from '../-private/components/form/submit-button.gts';
import { type UserData } from '../-private/types.ts';
import type WrButton628 from './button';
import WrButton from './button.gts';

export interface WrFormComponentSignature<
  DATA extends UserData,
  SUBMISSION_VALUE
> {
  Element: HTMLFormElement;
  Args: Omit<
    HeadlessFormComponentSignature<DATA, SUBMISSION_VALUE>['Args'],
    'dataMode'
  >;
  Blocks: {
    default: [
      {
        data: DATA | undefined;
        Input: WithBoundArgs<typeof InputFieldComponent628<DATA>, 'form'>;
        InputRow: WithBoundArgs<typeof InputRowComponent628<DATA>, 'form'>;
        Row: typeof RowComponent;
        FieldsetRow: typeof FieldsetRowComponent628;
        Textarea: WithBoundArgs<typeof TextareaComponent628<DATA>, 'form'>;
        TextareaRow: WithBoundArgs<
          typeof TextareaRowComponent628<DATA>,
          'form'
        >;
        Checkbox: WithBoundArgs<typeof CheckboxFieldComponent628<DATA>, 'form'>;
        RadioGroup: WithBoundArgs<
          typeof RadioGroupFieldComponent628<DATA>,
          'form'
        >;
        RadioRow: WithBoundArgs<typeof RadioRowFieldComponent628<DATA>, 'form'>;
        CancelButton: WithBoundArgs<typeof WrButton628, 'icon' | 'variant'>;
        SubmitButton: WithBoundArgs<
          typeof SubmitButtonComponent628<DATA>,
          'icon' | 'form'
        >;
        ToggleButtons: WithBoundArgs<typeof ToggleButtons628<DATA>, 'form'>;
      }
    ];
  };
}

export default class WrFormComponent<
  DATA extends UserData,
  SUBMISSION_VALUE
> extends Component<WrFormComponentSignature<DATA, SUBMISSION_VALUE>> {
  InputFieldComponent: typeof InputFieldComponent<DATA> =
    InputFieldComponent<DATA>;
  InputRowComponent: typeof InputRowComponent<DATA> = InputRowComponent<DATA>;
  TextareaComponent: typeof TextareaComponent<DATA> = TextareaComponent<DATA>;
  TextareaRowComponent: typeof TextareaRowComponent<DATA> =
    TextareaRowComponent<DATA>;
  CheckboxFieldComponent: typeof CheckboxFieldComponent<DATA> =
    CheckboxFieldComponent<DATA>;
  RadioGroupFieldComponent: typeof RadioGroupFieldComponent<DATA> =
    RadioGroupFieldComponent<DATA>;
  RadioRowFieldComponent: typeof RadioRowFieldComponent<DATA> =
    RadioRowFieldComponent<DATA>;
  SubmitButtonComponent: typeof SubmitButtonComponent<DATA> =
    SubmitButtonComponent<DATA>;
  ToggleButtons: typeof ToggleButtons<DATA> = ToggleButtons<DATA>;

  get validateOn() {
    const { validateOn } = this.args;
    return validateOn || 'input';
  }

  // Forcing the return type here because the return type of validateChangeset from ember-headless-form-changeset
  // is FormValidateCallback<EmberChangeset>
  // But when the type of @data passed to HeadlessForm is EmberChangeset, the field names are not properly validated
  // See https://github.com/CrowdStrike/ember-headless-form/issues/211
  validateChangeset() {
    return validateChangeset() as unknown as FormValidateCallback<DATA>;
  }

  get isPristine() {
    return (this.args.data as EmberChangeset).isPristine;
  }

  <template>
    <HeadlessForm
      @data={{@data}}
      @dataMode='mutable'
      @ignoreNativeValidation={{true}}
      @onSubmit={{@onSubmit}}
      @validateOn={{this.validateOn}}
      @revalidateOn='input'
      @validate={{(this.validateChangeset)}}
      ...attributes
      as |form|
    >
      {{yield
        (hash
          data=@data
          Row=RowComponent
          FieldsetRow=FieldsetRowComponent
          Input=(component this.InputFieldComponent form=form)
          InputRow=(component this.InputRowComponent form=form)
          Textarea=(component this.TextareaComponent form=form)
          TextareaRow=(component this.TextareaRowComponent form=form)
          Checkbox=(component this.CheckboxFieldComponent form=form)
          ToggleButtons=(component this.ToggleButtons form=form)
          RadioGroup=(component this.RadioGroupFieldComponent form=form)
          RadioRow=(component this.RadioRowFieldComponent form=form)
          CancelButton=(component WrButton variant='outlined' icon='Close')
          SubmitButton=(component
            this.SubmitButtonComponent
            icon='ArrowRight'
            form=form
            isDisabled=this.isPristine
          )
        )
      }}
    </HeadlessForm>
  </template>
}
